import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from '././ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';  
import './Main.css';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { 
  FaArrowLeft, 
  FaTrash, 
  FaPen, 
  FaPlus, 
  FaSearch,
  FaTimes,
  FaUser,
  FaUsers,
  FaSignInAlt,
  FaSignOutAlt,
  FaCircle,
  FaEllipsisV,
  FaCheck,
  FaUserCheck,
  FaUserTimes,
  FaArchive,
  FaEdit,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaIdCard,
  FaUserPlus,
  FaVenusMars,
  FaBirthdayCake,
  FaTag,
  FaEye,
  FaCalendarAlt,
  FaInfoCircle,
  FaFlag,
  FaFileAlt
} from 'react-icons/fa';
import './EvacuationMonitoring.css';
import Preloader from './Preloader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Event Report Component
function EventReportContent({ event, shelterName, currentUser, evacuees, shelterId, onClose }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateReportData = async () => {
      if (!event) return;

      try {
        // Get all check logs for this event
        const { data: eventLogs, error: logsError } = await supabase
          .from('evacuee_check_logs')
          .select(`
            *,
            evacuee:evacuees (
              first_name,
              last_name,
              age,
              gender,
              identification_number
            )
          `)
          .eq('event_id', event.id)
          .eq('shelter_id', shelterId)
          .order('check_in', { ascending: false });

        if (logsError) throw logsError;

        // Get all evacuees registered for this event (not just those with logs)
        console.log('📡 Fetching all evacuees registered for event:', event.id);
        const { data: eventEvacuees, error: evacueesError } = await supabase
          .from('evacuees')
          .select('id, first_name, last_name, event_id, status')
          .eq('event_id', event.id)
          .eq('status', 'active');

        console.log('📊 Event evacuees query result:', { 
          data: eventEvacuees, 
          error: evacueesError,
          count: eventEvacuees?.length || 0 
        });

        if (evacueesError) {
          console.error('❌ Error fetching event evacuees:', evacueesError);
        }

        // Also get evacuees who might be associated through logs but not direct event_id
        const evacueeIdsFromLogs = [...new Set(eventLogs?.map(log => log.evacuee_id) || [])];
        let additionalEvacuees = [];
        
        if (evacueeIdsFromLogs.length > 0) {
          console.log('📡 Fetching evacuees associated through logs:', evacueeIdsFromLogs);
          const { data: logEvacuees, error: logEvacueesError } = await supabase
            .from('evacuees')
            .select('id, first_name, last_name, event_id, status')
            .in('id', evacueeIdsFromLogs)
            .eq('status', 'active');

          if (!logEvacueesError && logEvacuees) {
            // Find evacuees who have logs for this event but aren't directly assigned to it
            additionalEvacuees = logEvacuees.filter(e => e.event_id !== event.id);
            console.log('📊 Additional evacuees from logs:', additionalEvacuees);
          }
        }

        // Combine all evacuees associated with this event
        const allEventEvacuees = [
          ...(eventEvacuees || []),
          ...additionalEvacuees
        ];
        
        // Remove duplicates
        const uniqueEventEvacuees = allEventEvacuees.filter((evacuee, index, self) => 
          index === self.findIndex(e => e.id === evacuee.id)
        );

        console.log('📊 All unique evacuees for this event:', uniqueEventEvacuees.length);

        // Calculate accurate statistics using comprehensive evacuee data
        const totalRecords = eventLogs?.length || 0;
        // Use number of ACTIVE evacuees registered in the shelter
        const activeRegisteredEvacuees = (evacuees || []).filter(e => e.status === 'active');
        const registeredIds = new Set(activeRegisteredEvacuees.map(e => e.id));
        const totalRegisteredEvacuees = activeRegisteredEvacuees.length;
        // Checked in at this shelter for this event (at least one check-in, regardless of checkout)
        const evacueeIdsWithCheckIns = new Set(
          (eventLogs || [])
            .filter(log => Boolean(log.check_in))
            .map(log => log.evacuee_id)
        );
        // Only count checked-in evacuees that are registered in this shelter
        const totalCheckedIn = [...evacueeIdsWithCheckIns].filter(id => registeredIds.has(id)).length;
        const currentlySheltered = (eventLogs || []).filter(
          log => Boolean(log.check_in) && !log.check_out && registeredIds.has(log.evacuee_id)
        ).length || 0; // Still checked in and registered here
        const evacueesThatDidNotCheckin = Math.max(totalRegisteredEvacuees - totalCheckedIn, 0); // Shelter registered - checked in
        
        console.log('📊 Report Statistics Debug:', {
          eventId: event.id,
          totalRegisteredEvacuees,
          uniqueEvacueesWithLogs: evacueeIdsWithCheckIns.size,
          totalCheckedIn,
          currentlySheltered,
          evacueesThatDidNotCheckin,
          totalRecords,
          eventEvacuees: eventEvacuees?.map(e => ({ id: e.id, name: `${e.first_name} ${e.last_name}`, event_id: e.event_id })),
          evacueeIdsWithLogs: [...new Set(eventLogs?.map(log => log.evacuee_id) || [])],
          additionalEvacuees: additionalEvacuees?.map(e => ({ id: e.id, name: `${e.first_name} ${e.last_name}`, event_id: e.event_id })),
          uniqueEventEvacuees: uniqueEventEvacuees?.map(e => ({ id: e.id, name: `${e.first_name} ${e.last_name}`, event_id: e.event_id }))
        });
        
        // Let's also check what evacuees exist in the main evacuees array for this event
        const mainArrayEvacuees = evacuees ? evacuees.filter(e => e.event_id === event.id) : [];
        console.log('📊 Main evacuees array for this event:', mainArrayEvacuees.map(e => ({ id: e.id, name: `${e.first_name} ${e.last_name}`, event_id: e.event_id })));
        
        // Use fallback calculation if database query didn't return enough evacuees
        const fallbackTotalRegistered = totalRegisteredEvacuees; // Registered in the shelter
        const fallbackDidNotCheckin = Math.max(fallbackTotalRegistered - totalCheckedIn, 0);
        
        console.log('📊 Fallback calculation:', {
          fallbackTotalRegistered,
          fallbackDidNotCheckin,
          usingFallback: fallbackTotalRegistered > totalRegisteredEvacuees
        });
        
        // Calculate average stay duration
        const completedVisits = eventLogs?.filter(log => log.check_in && log.check_out) || [];
        let avgStayHours = 0;
        if (completedVisits.length > 0) {
          const totalHours = completedVisits.reduce((sum, log) => {
            const checkIn = new Date(log.check_in);
            const checkOut = new Date(log.check_out);
            return sum + (checkOut - checkIn) / (1000 * 60 * 60);
          }, 0);
          avgStayHours = totalHours / completedVisits.length;
        }

        const avgStayDisplay = avgStayHours > 0 
          ? avgStayHours >= 24 
            ? `${Math.round(avgStayHours / 24 * 10) / 10} days`
            : `${Math.round(avgStayHours * 10) / 10} hours`
          : 'N/A';

        setReportData({
          statistics: {
            totalRecords,
            totalRegisteredEvacuees: fallbackTotalRegistered,
            uniqueEvacueesWithLogs: totalCheckedIn,
            totalCheckedIn,
            currentlySheltered,
            evacueesThatDidNotCheckin: fallbackDidNotCheckin,
            avgStayDisplay,
            avgStayHours
          },
          logs: eventLogs || []
        });
      } catch (error) {
        console.error('Error generating report data:', error);
        alert('Failed to generate report data.');
      } finally {
        setLoading(false);
      }
    };

    generateReportData();
  }, [event, shelterId, evacuees]);

  const handleGeneratePDF = () => {
    if (!reportData || !event) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 20;
    const palette = {
      primary: [23, 37, 84],
      secondary: [71, 85, 105],
      subtle: [120, 144, 156],
      divider: [226, 232, 240],
      fill: [249, 250, 251],
    };
    const setTextColor = (color) => doc.setTextColor(color[0], color[1], color[2]);
    const setDrawColor = (color) => doc.setDrawColor(color[0], color[1], color[2]);

    const now = new Date();
    const generatedOn = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
    
    let cursorY = 26;

    // Add logos (same as printable logs)
    try {
      const logo1 = `${window.location.origin}/GabaySakunaLogo.png`;
      doc.addImage(logo1, 'PNG', marginX, cursorY - 14, 14, 14);
    } catch (error) {
      console.warn('Failed to load primary logo for export.', error);
    }

    try {
      const logo2 = `${window.location.origin}/LegzapiCDRRMO.png`;
      doc.addImage(logo2, 'PNG', pageWidth - marginX - 14, cursorY - 14, 14, 14);
    } catch (error) {
      console.warn('Failed to load secondary logo for export.', error);
    }

    // Header exactly like printable logs
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    setTextColor(palette.primary);
    doc.text('GabaySakuna', pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    setTextColor(palette.secondary);
    doc.text(`Evacuation Event Report - ${event.name}`, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 6;

    setDrawColor(palette.divider);
    doc.setLineWidth(0.25);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 8;

    // Metadata section (your custom format)
    const lineHeight = 6;
    const metaLeft = [
      ['Evacuation Start', formatDateTime(event.start_time)],
      ['Evacuation End', event.end_time ? formatDateTime(event.end_time) : 'Ongoing'],
    ];
    const metaRight = [
      ['Generated On', generatedOn],
      ['Facility', shelterName],
    ];

    metaLeft.forEach((row, index) => {
      const y = cursorY + index * lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setTextColor(palette.secondary);
      doc.text(row[0], marginX, y);
      doc.setFont('helvetica', 'normal');
      setTextColor(palette.primary);
      doc.text(row[1], marginX + 32, y);
    });

    const rightColumnX = pageWidth / 2;
    metaRight.forEach((row, index) => {
      const y = cursorY + index * lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setTextColor(palette.secondary);
      doc.text(row[0], rightColumnX, y);
      doc.setFont('helvetica', 'normal');
      setTextColor(palette.primary);
      doc.text(row[1], rightColumnX + 32, y);
    });

    cursorY += lineHeight * Math.max(metaLeft.length, metaRight.length) + 6;
    setDrawColor(palette.divider);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 8;

    // Summary lines (your updated format)
    const summaryLines = [
      `Total Checked In: ${reportData.statistics.totalCheckedIn}`,
      `Evacuees that did not checkin: ${reportData.statistics.evacueesThatDidNotCheckin}`,
    ];

    summaryLines.forEach((line, index) => {
      const y = cursorY + index * lineHeight;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setTextColor(palette.secondary);
      doc.text(`• ${line}`, marginX, y);
    });

    cursorY += summaryLines.length * lineHeight + 8;
    const tableStartY = cursorY;

    // Table data (your updated format - no shelter column, Duration at end)
    const tableBody = reportData.logs.map(log => {
      const evacueeInfo = log.evacuee 
        ? `${log.evacuee.first_name} ${log.evacuee.last_name}`
        : 'Unknown';

      // Calculate duration
      let duration = '—';
      if (log.check_in && log.check_out) {
        const checkIn = new Date(log.check_in);
        const checkOut = new Date(log.check_out);
        const hours = (checkOut - checkIn) / (1000 * 60 * 60);
        duration = hours >= 24 
          ? `${Math.round(hours / 24 * 10) / 10} days`
          : `${Math.round(hours * 10) / 10} hours`;
      } else if (log.check_in) {
        duration = 'Still checked in';
      }

      return [
        evacueeInfo,
        log.check_in ? new Date(log.check_in).toLocaleString() : '—',
        log.check_out ? new Date(log.check_out).toLocaleString() : '—',
        duration
      ];
    });

    // Generate table (your updated headers)
    autoTable(doc, {
      head: [['Evacuee Name', 'Check-In', 'Check-Out', 'Duration']],
      body: tableBody,
      startY: tableStartY,
      margin: { left: marginX, right: marginX },
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: palette.divider,
        lineWidth: 0.2,
        textColor: [71, 85, 105]
      },
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: palette.primary,
        fontStyle: 'bold',
        lineColor: palette.divider,
        lineWidth: 0.2,
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: palette.fill
      },
      didDrawPage: (data) => {
        const footerY = pageHeight - 12;
        setDrawColor(palette.divider);
        doc.setLineWidth(0.2);
        doc.line(marginX, footerY - 3, pageWidth - marginX, footerY - 3);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        setTextColor(palette.subtle);
        const generatedBy = (() => {
          if (!currentUser) return 'System';
          const name = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim();
          if (name) return name;
          if (currentUser.role) {
            return currentUser.role.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
          }
          return 'System';
        })();
        
        doc.text(`Generated by ${generatedBy} for ${event.name}`, marginX, footerY);
        doc.text(`Page ${data.pageNumber}`, pageWidth - marginX, footerY, { align: 'right' });
      }
    });

    // Save with same naming convention
    let fileName = `Evacuation Event Report - ${event.name}`;
    fileName = fileName.replace(/[^a-zA-Z0-9 _\-()]/g, '').replace(/ +/g, ' ').trim();
    doc.save(`${fileName || 'evacuation-event-report'}.pdf`);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        color: '#6b7280',
        fontSize: 14
      }}>
        <div className="loading-spinner" style={{ marginRight: 8, width: 20, height: 20 }}></div>
        Loading report data...
      </div>
    );
  }

  if (!reportData) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        color: '#dc2626',
        fontSize: 14,
        background: '#fef2f2',
        borderRadius: 6,
        padding: 16
      }}>
        Failed to load report data.
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      lineHeight: '1.4',
      color: '#374151',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Document Preview Header */}
      <div style={{
        background: '#f8fafc',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#64748b',
          marginBottom: '8px'
        }}>
          📄 Document Preview
        </div>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#0f172a'
        }}>
          This is how your PDF will look when generated
        </div>
      </div>

      {/* PDF Document Preview */}
      <div style={{
        padding: '40px',
        background: '#fff',
        minHeight: '600px'
      }}>
        {/* Document Header (as it will appear in PDF) */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          {/* Left Logo */}
          <div style={{
            position: 'absolute',
            left: '0',
            top: '0',
            width: '40px',
            height: '40px'
          }}>
            <img 
              src="/GabaySakunaLogo.png" 
              alt="GabaySakuna Logo"
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          {/* Right Logo */}
          <div style={{
            position: 'absolute',
            right: '0',
            top: '0',
            width: '40px',
            height: '40px'
          }}>
            <img 
              src="/LegzapiCDRRMO.png" 
              alt="Legzapi CDRRMO Logo"
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#172554',
            marginBottom: '8px',
            marginTop: '20px'
          }}>
            GabaySakuna
          </div>
          <div style={{
            fontSize: '14px',
            color: '#64748b'
          }}>
            Evacuation Event Report - {event.name}
          </div>
        </div>

        {/* Metadata Section (as it will appear in PDF) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          marginBottom: '20px',
          fontSize: '12px'
        }}>
          <div>
            <div style={{ marginBottom: '6px' }}>
              <strong style={{ color: '#64748b' }}>Evacuation Start:</strong>{' '}
              <span style={{ color: '#172554' }}>{formatDateTime(event.start_time)}</span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong style={{ color: '#64748b' }}>Evacuation End:</strong>{' '}
              <span style={{ color: '#172554' }}>{event.end_time ? formatDateTime(event.end_time) : 'Ongoing'}</span>
            </div>
          </div>
          <div>
            <div style={{ marginBottom: '6px' }}>
              <strong style={{ color: '#64748b' }}>Generated On:</strong>{' '}
              <span style={{ color: '#172554' }}>{new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong style={{ color: '#64748b' }}>Facility:</strong>{' '}
              <span style={{ color: '#172554' }}>{shelterName}</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{
          height: '1px',
          background: '#e5e7eb',
          margin: '16px 0'
        }}></div>

        {/* Summary Statistics (as it will appear in PDF) */}
        <div style={{
          marginBottom: '20px',
          fontSize: '12px'
        }}>
          <div style={{ marginBottom: '6px' }}>
            <strong style={{ color: '#64748b' }}>• Total Checked In:</strong>{' '}
            <span style={{ color: '#172554' }}>{reportData.statistics.totalCheckedIn}</span>
          </div>
          <div style={{ marginBottom: '6px' }}>
            <strong style={{ color: '#64748b' }}>• Evacuees that did not checkin:</strong>{' '}
            <span style={{ color: '#172554' }}>{reportData.statistics.evacueesThatDidNotCheckin}</span>
          </div>
        </div>

        {/* Table Preview (as it will appear in PDF) */}
        {reportData.logs.length > 0 ? (
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '20px'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '11px'
            }}>
              <thead>
                <tr style={{ 
                  background: '#f1f5f9',
                  color: '#172554'
                }}>
                  <th style={{ 
                    padding: '8px 12px', 
                    textAlign: 'left',
                    fontWeight: '600',
                    borderRight: '1px solid #e5e7eb'
                  }}>
                    Evacuee Name
                  </th>
                  <th style={{ 
                    padding: '8px 12px', 
                    textAlign: 'left',
                    fontWeight: '600',
                    borderRight: '1px solid #e5e7eb'
                  }}>
                    Check-In
                  </th>
                  <th style={{ 
                    padding: '8px 12px', 
                    textAlign: 'left',
                    fontWeight: '600',
                    borderRight: '1px solid #e5e7eb'
                  }}>
                    Check-Out
                  </th>
                  <th style={{ 
                    padding: '8px 12px', 
                    textAlign: 'left',
                    fontWeight: '600'
                  }}>
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData.logs.slice(0, 10).map((log, idx) => {
                  const checkIn = log.check_in ? new Date(log.check_in) : null;
                  const checkOut = log.check_out ? new Date(log.check_out) : null;
                  let duration = '—';
                  
                  if (checkIn && checkOut) {
                    const hours = (checkOut - checkIn) / (1000 * 60 * 60);
                    duration = hours >= 24 
                      ? `${Math.round(hours / 24 * 10) / 10} days`
                      : `${Math.round(hours * 10) / 10} hours`;
                  } else if (checkIn) {
                    duration = 'Still checked in';
                  }

                  return (
                    <tr key={idx} style={{ 
                      borderBottom: '1px solid #f3f4f6',
                      background: idx % 2 === 0 ? '#fff' : '#f9fafb'
                    }}>
                      <td style={{ 
                        padding: '8px 12px',
                        borderRight: '1px solid #f3f4f6',
                        color: '#1a1a1a'
                      }}>
                        {log.evacuee?.first_name} {log.evacuee?.last_name}
                      </td>
                      <td style={{ 
                        padding: '8px 12px',
                        borderRight: '1px solid #f3f4f6',
                        color: '#64748b',
                        fontFamily: 'monospace',
                        fontSize: '10px'
                      }}>
                        {formatDateTime(log.check_in)}
                      </td>
                      <td style={{ 
                        padding: '8px 12px',
                        borderRight: '1px solid #f3f4f6',
                        color: '#64748b',
                        fontFamily: 'monospace',
                        fontSize: '10px'
                      }}>
                        {formatDateTime(log.check_out)}
                      </td>
                      <td style={{ 
                        padding: '8px 12px',
                        color: checkOut ? '#059669' : '#d97706',
                        fontWeight: '500'
                      }}>
                        {duration}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {reportData.logs.length > 10 && (
              <div style={{
                padding: '12px',
                background: '#f8fafc',
                textAlign: 'center',
                fontSize: '11px',
                color: '#64748b',
                borderTop: '1px solid #e5e7eb'
              }}>
                ... and {reportData.logs.length - 10} more entries (will be included in PDF)
              </div>
            )}
          </div>
        ) : (
          <div style={{
            background: '#f9fafb',
            border: '2px dashed #d1d5db',
            borderRadius: 8,
            padding: '30px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '14px'
          }}>
            No check-in/check-out records found for this event.
          </div>
        )}

        {/* Footer Preview (as it will appear in PDF) */}
        <div style={{
          marginTop: '20px',
          paddingTop: '12px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '10px',
          color: '#94a3b8',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <span>
            Generated by {(() => {
              if (!currentUser) return 'System';
              const name = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim();
              if (name) return name;
              if (currentUser.role) {
                return currentUser.role.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
              }
              return 'System';
            })()} for {event.name}
          </span>
          <span>Page 1</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="report-actions" style={{ 
        display: 'flex', 
        gap: '12px', 
        justifyContent: 'flex-end',
        paddingTop: '20px',
        borderTop: '1px solid #e5e7eb',
        marginTop: '20px'
      }}>
        <button
          onClick={onClose}
          className="btn btn-secondary"
        >
          Close
        </button>
        <button
          onClick={handleGeneratePDF}
          className="btn btn-primary"
        >
          <FaFileAlt /> Generate PDF
        </button>
      </div>
    </div>
  );
}

function EvacuationMonitoring() {
  const navigate = useNavigate();
  const { id: shelterId } = useParams();
  
  console.log('🏠 EvacuationMonitoring component rendered with shelterId:', shelterId);
  const [shelterName, setShelterName] = useState('...');
  const [evacuees, setEvacuees] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([{
    firstName: '',
    lastName: '',
    age: '',
    gender: '', 
    identificationNumber: '',
  }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [evacueeListLoading, setEvacueeListLoading] = useState(true);
  const [evacueeListError, setEvacueeListError] = useState('');
  const [familyUUID, setFamilyUUID] = useState(uuidv4());
  const [selectedEvacuees, setSelectedEvacuees] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentEvacuee, setCurrentEvacuee] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedInSearchTerm, setCheckedInSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('checkedout');
  const [currentTab, setCurrentTab] = useState('evacuees');
  const itemsPerPage = 10;
  const [evacueePage, setEvacueePage] = useState(1);
  const [checkedInPage, setCheckedInPage] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [updatingEvacuees, setUpdatingEvacuees] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [activeEventId, setActiveEventId] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState('');
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [eventFormError, setEventFormError] = useState('');
  const [completingEvent, setCompletingEvent] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [eventForm, setEventForm] = useState({
    name: '',
    startDate: '',
    description: ''
  });
  const activeEvent = useMemo(
    () => events.find(event => event.id === activeEventId) || null,
    [events, activeEventId]
  );
  const isEventSelected = Boolean(activeEventId);
  const isEventActive = activeEvent ? activeEvent.status === 'active' : false;
  const addButtonDisabled = !isEventSelected || !isEventActive;
  const canManageAttendance = !addButtonDisabled;
  
  // Allow checkout even after event completion (for evacuees still checked in)
  const canCheckOut = isEventSelected; // Can check out if any event is selected (active or completed)
  
  // Check if there's already an active event (to disable Create Event button)
  const hasActiveEvent = events.some(event => event.status === 'active');

  // Dashboard statistics
  const dashboardStats = useMemo(() => {
    const activeEvacuees = evacuees.filter(e => e.status === 'active');
    
    // Number of Evacuees (active only)
    const numberOfEvacuees = activeEvacuees.length;
    
    // Number of Families (unique family_ids from active evacuees)
    const uniqueFamilyIds = new Set(
      activeEvacuees
        .map(e => e.family_id)
        .filter(Boolean)
    );
    const numberOfFamilies = uniqueFamilyIds.size;
    
    // Checked In (evacuees with latest_log that has check_in but no check_out)
    const checkedIn = activeEvacuees.filter(e => {
      const log = e.latest_log;
      return log && log.check_in && !log.check_out;
    }).length;
    
    // Checked Out (evacuees with latest_log that has both check_in and check_out)
    const checkedOut = activeEvacuees.filter(e => {
      const log = e.latest_log;
      return log && log.check_in && log.check_out;
    }).length;
    
    return {
      numberOfEvacuees,
      numberOfFamilies,
      checkedIn,
      checkedOut
    };
  }, [evacuees]);


  useEffect(() => {
    document.title = "Evacuation Shelter Monitoring";
  }, []);

  const fetchShelterDetails = useCallback(async () => {
    if (!shelterId) {
      setShelterName('No Shelter ID Provided');
      return;
    }
    const { data, error } = await supabase
      .from('evacuation_shelters')
      .select('shelter_name, accepted_occupants, capacity')
      .eq('id', shelterId)
      .single();

    if (error) {
      console.error('Error fetching shelter details:', error);
      setShelterName('Error loading shelter details');
    } else if (data) {
      setShelterName(data.shelter_name);
    }
  }, [shelterId]);

  const fetchEvents = useCallback(async () => {
    if (!shelterId) {
      setEvents([]);
      setActiveEventId(null);
      return;
    }

    setEventLoading(true);
    setEventError('');

    try {
      const { data, error } = await supabase
        .from('evacuation_events')
        .select('*')
        .eq('shelter_id', shelterId)
        .order('start_time', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      const fetchedEvents = data || [];
      setEvents(fetchedEvents);
      setActiveEventId(prevActive => {
        if (prevActive && fetchedEvents.some(event => event.id === prevActive)) {
          return prevActive;
        }

        const existingActive = fetchedEvents.find(event => event.status === 'active');
        if (existingActive) return existingActive.id;

        return fetchedEvents.length > 0 ? fetchedEvents[0].id : null;
      });
    } catch (err) {
      console.error('Error fetching evacuation events:', err);
      setEventError('Failed to load evacuation events.');
      setEvents([]);
      setActiveEventId(null);
    } finally {
      setEventLoading(false);
    }
  }, [shelterId]);

  const fetchEvacuees = useCallback(async () => {
    console.log('🔄 fetchEvacuees called with shelterId:', shelterId);
    setEvacueeListLoading(true);
    setEvacueeListError('');
    
    if (!shelterId) {
      console.error('❌ No shelterId provided to fetchEvacuees');
      setEvacueeListError('Shelter ID is missing. Cannot fetch evacuees.');
      setEvacueeListLoading(false);
      return;
    }

    try {
      console.log('📡 Fetching evacuees from database for shelter:', shelterId);
      const { data: evacueesData, error: evacueesError } = await supabase
        .from('evacuees')
        .select(`
          *,
          shelter:evacuation_shelters (
            address
          )
        `)
        .eq('shelter_id', shelterId)
        .order('first_name', { ascending: true });

      console.log('📊 Evacuees query result:', { 
        data: evacueesData, 
        error: evacueesError,
        count: evacueesData?.length || 0 
      });

      if (evacueesError) {
        console.error('❌ Error in evacuees query:', evacueesError);
        throw evacueesError;
      }

      const evacueeIds = evacueesData.map(e => e.id);
      console.log('🆔 Evacuee IDs found:', evacueeIds);
      
      // Fetch event information separately if evacuees have event_id
      let eventsMap = {};
      const evacueeEventIds = [...new Set(evacueesData.map(e => e.event_id).filter(Boolean))];
      if (evacueeEventIds.length > 0) {
        console.log('📡 Fetching event information for event IDs:', evacueeEventIds);
        const { data: eventsData, error: eventsError } = await supabase
          .from('evacuation_events')
          .select('id, name, status')
          .in('id', evacueeEventIds);
        
        console.log('📊 Events query result:', { 
          data: eventsData, 
          error: eventsError,
          count: eventsData?.length || 0 
        });

        if (!eventsError && eventsData) {
          eventsData.forEach(event => {
            eventsMap[event.id] = event;
          });
          console.log('📋 Events map created for', Object.keys(eventsMap).length, 'events');
        } else if (eventsError) {
          console.warn('⚠️ Error fetching events (non-critical):', eventsError);
        }
      }
      
      let logsMap = {};
      if (evacueeIds.length > 0) {
        console.log('📡 Fetching check logs for evacuees...');
        const { data: logsData, error: logsError } = await supabase
          .from('evacuee_check_logs')
          .select('*')
          .in('evacuee_id', evacueeIds)
          .eq('shelter_id', shelterId);
        
        console.log('📊 Check logs query result:', { 
          data: logsData, 
          error: logsError,
          count: logsData?.length || 0 
        });

        if (logsError) {
          console.warn('⚠️ Error fetching check logs (non-critical):', logsError);
        }

        if (!logsError && logsData) {
          console.log('🔄 Processing check logs...');
          evacueeIds.forEach(evacueeId => {
            const logs = logsData.filter(l => l.evacuee_id === evacueeId);
            if (logs.length > 0) {
              logs.sort((a, b) => new Date(b.check_in) - new Date(a.check_in));
              logsMap[evacueeId] = logs[0];
            }
          });
          console.log('📋 Processed logs map:', Object.keys(logsMap).length, 'evacuees have logs');
        }
      } else {
        console.log('ℹ️ No evacuees found, skipping check logs fetch');
      }

      console.log('🔄 Processing evacuee data...');
      const activeEvacueesForCount = evacueesData.filter(e => e.status === 'active');
      console.log('👥 Active evacuees count:', activeEvacueesForCount.length);
      
      const familyCountMap = {};
      activeEvacueesForCount.forEach(evacuee => {
        if (evacuee.family_id) {
          familyCountMap[evacuee.family_id] = (familyCountMap[evacuee.family_id] || 0) + 1;
        }
      });
      console.log('👨‍👩‍👧‍👦 Family count map:', familyCountMap);

      const evacueesWithCounts = evacueesData.map(evacuee => ({
        ...evacuee,
        family_count: evacuee.family_id ? familyCountMap[evacuee.family_id] || 0 : 1,
        latest_log: logsMap[evacuee.id] || null,
        event_info: evacuee.event_id ? eventsMap[evacuee.event_id] || null : null
      }));
      
      console.log('✅ Successfully processed evacuees:', evacueesWithCounts.length, 'total evacuees');
      setEvacuees(evacueesWithCounts);
    } catch (err) {
      console.error('❌ Error in fetchEvacuees:', {
        error: err,
        message: err.message,
        details: err.details,
        hint: err.hint,
        code: err.code
      });
      setEvacueeListError(`Failed to load evacuee list: ${err.message || 'Unknown error'}`);
      setEvacuees([]);
    } finally {
      console.log('🏁 fetchEvacuees completed, setting loading to false');
      setEvacueeListLoading(false);
    }
  }, [shelterId]);

  const getDefaultEventStart = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  useEffect(() => {
    console.log('🚀 Initial useEffect running - mounting component with shelterId:', shelterId);
    fetchShelterDetails();
    fetchEvents();
    fetchEvacuees();

    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();

    const evacueesChannel = supabase
      .channel('public:evacuation_monitoring_evacuees')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuees', filter: `shelter_id=eq.${shelterId}` },
        (payload) => {
          console.log('📡 Evacuee change received via real-time subscription!', payload);
          console.log('🔄 Calling fetchEvacuees due to evacuee table change');
          fetchEvacuees();
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuee_check_logs', filter: `shelter_id=eq.${shelterId}` },
        (payload) => {
          console.log('📡 Evacuee check log change received via real-time subscription!', payload);
          console.log('🔄 Calling fetchEvacuees due to check log change');
          fetchEvacuees();
        }
      )
      .subscribe();

    const shelterChannel = supabase
      .channel('public:evacuation_monitoring_shelter')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuation_shelters', filter: `id=eq.${shelterId}` },
        (payload) => {
          console.log('Shelter change received!', payload);
          fetchShelterDetails();
        }
      )
      .subscribe();

    const eventsChannel = supabase
      .channel('public:evacuation_monitoring_events')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuation_events', filter: `shelter_id=eq.${shelterId}` },
        (payload) => {
          console.log('Event change received!', payload);
          fetchEvents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(evacueesChannel);
      supabase.removeChannel(shelterChannel);
      supabase.removeChannel(eventsChannel);
    };
  }, [shelterId, fetchShelterDetails, fetchEvents, fetchEvacuees]);

  useEffect(() => {
    console.log('🔄 fetchEvacuees useEffect triggered');
    fetchEvacuees();
  }, [fetchEvacuees]);

  useEffect(() => {
    setSelectedEvacuees([]);
    setSearchTerm('');
    setCheckedInSearchTerm('');
    setEvacueePage(1);
    setCheckedInPage(1);
  }, [activeEventId]);

  const handleSelectEvent = (eventId) => {
    if (!eventId) {
      setActiveEventId(null);
      return;
    }
    setActiveEventId(eventId);
  };

  const handleOpenEventModal = () => {
    setEventForm({
      name: '',
      startDate: getDefaultEventStart(),
      description: ''
    });
    setEventFormError('');
    setIsEventModalOpen(true);
  };

  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    setEventForm({
      name: '',
      startDate: '',
      description: ''
    });
    setEventFormError('');
  };

  const handleEventFormChange = (e) => {
    const { name, value } = e.target;
    setEventForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!shelterId) {
      setEventFormError('Shelter ID is missing. Cannot create event.');
      return;
    }
    if (!eventForm.name.trim()) {
      setEventFormError('Event name is required.');
      return;
    }

    setCreatingEvent(true);
    setEventFormError('');

    try {
      const startDateIso = eventForm.startDate
        ? new Date(eventForm.startDate).toISOString()
        : new Date().toISOString();

      await supabase
        .from('evacuation_events')
        .update({ status: 'archived' })
        .eq('shelter_id', shelterId)
        .eq('status', 'active');

      const { data, error } = await supabase
        .from('evacuation_events')
        .insert({
          shelter_id: shelterId,
          name: eventForm.name.trim(),
          start_time: startDateIso,
          description: eventForm.description.trim() || null,
          status: 'active'
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setActiveEventId(data.id);
        await updateAcceptedOccupants(shelterId, data.id);
      }

      handleCloseEventModal();
      fetchEvents();
    } catch (err) {
      console.error('Error creating evacuation event:', err);
      setEventFormError(err.message || 'Failed to create evacuation event.');
    } finally {
      setCreatingEvent(false);
    }
  };

  const handleCompleteEvent = async () => {
    if (!activeEvent || activeEvent.status !== 'active') return;
    
    // Check if there are still evacuees checked in
    const currentlyCheckedIn = checkedInForActiveEvent.length;
    
    if (currentlyCheckedIn > 0) {
      const confirmMessage = `⚠️ There are still ${currentlyCheckedIn} evacuees checked in for this event.\n\nCompleting the event will:\n• End further check-ins\n• Automatically check out all remaining evacuees\n• Mark the event as completed\n\nDo you want to complete "${activeEvent.name}" and check out all remaining evacuees?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    } else {
      if (!window.confirm(`Mark "${activeEvent.name}" as completed? This will end further check-ins.`)) {
        return;
      }
    }

    setCompletingEvent(true);
    try {
      const completedAt = new Date().toISOString();
      
      // If there are evacuees still checked in, check them out first
      if (currentlyCheckedIn > 0) {
        console.log('🔄 Auto-checking out remaining evacuees before completing event');
        
        for (const evacuee of checkedInForActiveEvent) {
          const { data: logs, error: logError } = await supabase
            .from('evacuee_check_logs')
            .select('id')
            .eq('evacuee_id', evacuee.id)
            .eq('shelter_id', shelterId)
            .eq('event_id', activeEventId)
            .is('check_out', null)
            .order('check_in', { ascending: false })
            .limit(1);
            
          if (logs && logs.length > 0) {
            const logId = logs[0].id;
            await supabase
              .from('evacuee_check_logs')
              .update({ check_out: completedAt })
              .eq('id', logId);
              
            // Add audit log for automatic checkout
            if (currentUser) {
              await supabase.from('audit_logs').insert([{
                user_id: currentUser.id,
                action: 'auto checkout on event completion',
                target_id: evacuee.id,
                target_type: 'EvacuationMonitoring',
                details: { 
                  shelter_id: shelterId, 
                  event_id: activeEventId, 
                  check_out: completedAt,
                  reason: 'Event completed'
                }
              }]);
            }
          }
        }
        
        console.log('✅ Auto-checked out all remaining evacuees');
      }

      // Now complete the event
      const { error } = await supabase
        .from('evacuation_events')
        .update({
          status: 'completed',
          end_time: completedAt
        })
        .eq('id', activeEvent.id);

      if (error) throw error;

      await updateAcceptedOccupants(shelterId, activeEvent.id);
      fetchEvents();
      fetchEvacuees(); // Refresh evacuee data to show updated check-out status
      fetchShelterDetails();
      
      if (currentlyCheckedIn > 0) {
        alert(`Event completed successfully!\n\nAll ${currentlyCheckedIn} remaining evacuees have been automatically checked out.`);
      } else {
        alert('Event marked as completed successfully!');
      }
    } catch (err) {
      console.error('Error completing evacuation event:', err);
      alert('Failed to complete event.');
    } finally {
      setCompletingEvent(false);
    }
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    setFamilyMembers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const addFamilyMember = () => {
    setFamilyMembers(prev => [...prev, {
      firstName: '',
      lastName: '',
      age: '',
      gender: '',
      identificationNumber: '',
    }]);
  };

  const removeFamilyMember = (index) => {
    setFamilyMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleCheckboxChange = (id) => {
    setSelectedEvacuees(prev => {
      if (prev.includes(id)) {
        return prev.filter(evacueeId => evacueeId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllClick = (e) => {
    const currentEvacuees = currentTab === 'evacuees' ? filteredEvacuees : filteredCheckedIn;
    const currentPage = currentTab === 'evacuees' ? evacueePage : checkedInPage;
    const paginatedEvacuees = currentEvacuees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    
    if (e.target.checked) {
      // Select all items on current page
      const pageEvacueeIds = paginatedEvacuees.map(evacuee => evacuee.id);
      setSelectedEvacuees(prev => {
        // Combine existing selections with new page selections, removing duplicates
        const newSelection = [...new Set([...prev, ...pageEvacueeIds])];
        return newSelection;
      });
    } else {
      // Deselect all items on current page
      const pageEvacueeIds = paginatedEvacuees.map(evacuee => evacuee.id);
      setSelectedEvacuees(prev => prev.filter(id => !pageEvacueeIds.includes(id)));
    }
  };

  const handleArchiveSelected = async () => {
    if (selectedEvacuees.length === 0) {
      alert('Please select evacuees to archive.');
      return;
    }

    if (!window.confirm(`Are you sure you want to archive ${selectedEvacuees.length} selected evacuee(s)?`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('evacuees')
        .update({ status: 'archived' })
        .in('id', selectedEvacuees);

      if (updateError) throw updateError;

      if (currentUser) {
        for (const id of selectedEvacuees) {
          await supabase.from('audit_logs').insert([{
            user_id: currentUser.id,
            action: 'archived evacuee',
            target_id: id,
            target_type: 'EvacuationMonitoring',
            details: { status: 'archived' }
          }]);
        }
      }

      alert('Selected evacuee(s) archived successfully!');
      setSelectedEvacuees([]);
      fetchEvacuees();
    } catch (err) {
      console.error('Error archiving evacuees:', err);
      setError(`Failed to archive evacuee(s): ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvacuee = async (evacueeId) => {
    setEditLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('evacuees')
        .select('*')
        .eq('id', evacueeId)
        .single();

      if (error) throw error;

      setCurrentEvacuee(data);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Error fetching evacuee for edit:', err);
      setError(`Failed to load evacuee for edit: ${err.message || 'Unknown error'}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleViewDetails = async (evacueeId) => {
    setEditLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('evacuees')
        .select('*')
        .eq('id', evacueeId)
        .single();

      if (error) throw error;

      // Find the evacuee in our current list to get the latest log data
      const evacueeWithLogs = evacuees.find(e => e.id === evacueeId);
      setCurrentEvacuee({
        ...data,
        latest_log: evacueeWithLogs?.latest_log || null,
        family_count: evacueeWithLogs?.family_count || 1,
        event_info: evacueeWithLogs?.event_info || null
      });
      setIsDetailModalOpen(true);
    } catch (err) {
      console.error('Error fetching evacuee details:', err);
      setError(`Failed to load evacuee details: ${err.message || 'Unknown error'}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentEvacuee(null);
    setError('');
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setFamilyMembers([{
      firstName: '',
      lastName: '',
      age: '',
      gender: '',
      identificationNumber: '',
    }]);
    setFamilyUUID(uuidv4());
    setError('');
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setCurrentEvacuee(null);
    setError('');
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentEvacuee(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setError('');

    if (!currentEvacuee) return;

    try {
      const { error: updateError } = await supabase
        .from('evacuees')
        .update({
          first_name: currentEvacuee.first_name,
          last_name: currentEvacuee.last_name,
          age: parseInt(currentEvacuee.age),
          gender: currentEvacuee.gender,
          identification_number: currentEvacuee.identification_number || null,
        })
        .eq('id', currentEvacuee.id);

      if (updateError) throw updateError;

      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'edited evacuee',
          target_id: currentEvacuee.id,
          target_type: 'EvacuationMonitoring',
          details: currentEvacuee
        }]);
      }

      alert('Evacuee updated successfully!');
      handleCloseEditModal();
      fetchEvacuees();
    } catch (err) {
      console.error('Error saving evacuee:', err);
      setError(`Failed to save evacuee: ${err.message || 'Unknown error'}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);
    setError('');

    if (!shelterId) {
      setError('Shelter ID is missing. Cannot add evacuees.');
      setRegisterLoading(false);
      return;
    }
    if (!activeEventId || !activeEvent) {
      setError('Select an evacuation event before registering evacuees.');
      setRegisterLoading(false);
      return;
    }
    if (activeEvent.status !== 'active') {
      setError('The selected evacuation event is not active. Activate or create a new event to continue.');
      setRegisterLoading(false);
      return;
    }

    try {
      const { data: shelterData, error: shelterError } = await supabase
        .from('evacuation_shelters')
        .select('accepted_occupants, capacity')
        .eq('id', shelterId)
        .single();

      if (shelterError) throw new Error('Failed to fetch shelter capacity');
      
      if (shelterData.accepted_occupants + familyMembers.length > shelterData.capacity) {
        throw new Error(`Shelter can only accommodate ${shelterData.capacity - shelterData.accepted_occupants} more people`);
      }

      const evacueesToInsert = familyMembers.map(member => ({
        shelter_id: shelterId,
        first_name: member.firstName,
        last_name: member.lastName,
        age: parseInt(member.age),
        gender: member.gender,
        identification_number: member.identificationNumber || null,
        family_id: familyUUID,
        status: 'active',
        event_id: activeEventId
      }));

      const { data: evacueeInsertData, error: evacueeInsertError } = await supabase
        .from('evacuees')
        .insert(evacueesToInsert)
        .select();

      if (evacueeInsertError) {
        console.error('Evacuee insert error:', evacueeInsertError);
        throw new Error(`Failed to add evacuees: ${evacueeInsertError.message}`);
      }
      
      if (evacueeInsertData) {
        const checkInLogs = evacueeInsertData.map(evacuee => ({
          evacuee_id: evacuee.id,
          shelter_id: shelterId,
          check_in: new Date().toISOString(),
          event_id: activeEventId,
        }));

        const { error: logInsertError } = await supabase
          .from('evacuee_check_logs')
          .insert(checkInLogs);

        if (logInsertError) {
          console.error('Error creating check-in logs:', logInsertError);
          throw new Error('Evacuees were added, but failed to create check-in logs.');
        }

        if (currentUser) {
          for (const evacuee of evacueeInsertData) {
            await supabase.from('audit_logs').insert([{
              user_id: currentUser.id,
              action: 'added evacuee',
              target_id: evacuee.id,
              target_type: 'EvacuationMonitoring',
              details: evacuee
            }]);
          }
        }
      }

      await updateAcceptedOccupants(shelterId, activeEventId);

      alert('Family members added successfully!');
      handleCloseAddModal();
      fetchEvacuees();
      fetchShelterDetails();
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCheckIn = async (evacueeId) => {
    if (!activeEventId || !activeEvent) {
      alert('Select an active evacuation event before checking in evacuees.');
      return;
    }
    if (activeEvent.status !== 'active') {
      alert('Cannot check in evacuees to an event that is not active.');
      return;
    }

    const evacueeRecord = evacuees.find(e => e.id === evacueeId);
    if (evacueeRecord && isCheckedInAnywhere(evacueeRecord)) {
      alert('Evacuee is already checked in. Please check them out first.');
      return;
    }

    setUpdatingEvacuees(prev => new Set(prev).add(evacueeId));
    setLoading(true);
    
    try {
      const checkInTime = new Date().toISOString();

      await supabase
        .from('evacuee_check_logs')
        .insert({
          evacuee_id: evacueeId,
          shelter_id: shelterId,
          check_in: checkInTime,
          event_id: activeEventId,
        });
      
      await supabase
        .from('evacuees')
        .update({ event_id: activeEventId })
        .eq('id', evacueeId);
      
      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'checkin evacuee',
          target_id: evacueeId,
          target_type: 'EvacuationMonitoring',
          details: { shelter_id: shelterId, event_id: activeEventId, check_in: checkInTime }
        }]);
      }
      
      setEvacuees(prev => prev.map(evacuee => {
        if (evacuee.id === evacueeId) {
          return {
            ...evacuee,
            event_id: activeEventId,
            event_info: activeEvent ? {
              id: activeEvent.id,
              name: activeEvent.name,
              status: activeEvent.status
            } : evacuee.event_info,
            latest_log: {
              ...(evacuee.latest_log || {}),
              check_in: checkInTime,
              check_out: null,
              event_id: activeEventId
            }
          };
        }
        return evacuee;
      }));
      
      await updateAcceptedOccupants(shelterId, activeEventId);
      fetchShelterDetails();
    } catch (err) {
      alert('Failed to check in evacuee.');
    } finally {
      setUpdatingEvacuees(prev => {
        const newSet = new Set(prev);
        newSet.delete(evacueeId);
        return newSet;
      });
      setLoading(false);
    }
  };

  const handleCheckOut = async (evacueeId) => {
    if (!activeEventId) {
      alert('Select an evacuation event before checking out evacuees.');
      return;
    }
    if (!activeEvent) {
      alert('No evacuation event selected.');
      return;
    }

    const evacueeRecord = evacuees.find(e => e.id === evacueeId);
    if (!evacueeRecord || !isCheckedInCurrentEvent(evacueeRecord)) {
      alert('This evacuee is not checked in for the selected event.');
      return;
    }

    setUpdatingEvacuees(prev => new Set(prev).add(evacueeId));
    setLoading(true);
    
    try {
      const checkOutTime = new Date().toISOString();
      const { data: logs, error } = await supabase
        .from('evacuee_check_logs')
        .select('id')
        .eq('evacuee_id', evacueeId)
        .eq('shelter_id', shelterId)
        .eq('event_id', activeEventId)
        .is('check_out', null)
        .order('check_in', { ascending: false })
        .limit(1);
        
      if (error || !logs || logs.length === 0) throw new Error('No open check-in log found.');
      
      const logId = logs[0].id;
      await supabase
        .from('evacuee_check_logs')
        .update({ check_out: checkOutTime })
        .eq('id', logId);
        
      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'checkout evacuee',
          target_id: evacueeId,
          target_type: 'EvacuationMonitoring',
          details: { shelter_id: shelterId, event_id: activeEventId, check_out: checkOutTime }
        }]);
      }

      setEvacuees(prev => prev.map(evacuee => {
        if (evacuee.id === evacueeId && evacuee.latest_log) {
          return {
            ...evacuee,
            latest_log: {
              ...(evacuee.latest_log || {}),
              check_out: checkOutTime,
              event_id: activeEventId
            }
          };
        }
        return evacuee;
      }));
      
      await updateAcceptedOccupants(shelterId, activeEventId);
      fetchShelterDetails();
    } catch (err) {
      alert('Failed to check out evacuee.');
    } finally {
      setUpdatingEvacuees(prev => {
        const newSet = new Set(prev);
        newSet.delete(evacueeId);
        return newSet;
      });
      setLoading(false);
    }
  };

  const handleCheckOutSelected = async () => {
    if (selectedEvacuees.length === 0) return;
    if (!activeEventId) {
      alert('Select an evacuation event before checking out evacuees.');
      return;
    }
    if (!activeEvent) {
      alert('No evacuation event selected.');
      return;
    }

    const eligibleEvacuees = selectedEvacuees.filter(id => {
      const evac = evacuees.find(e => e.id === id);
      return evac && isCheckedInCurrentEvent(evac);
    });

    if (eligibleEvacuees.length === 0) {
      alert('None of the selected evacuees are checked in for this event.');
      return;
    }
    
    setUpdatingEvacuees(prev => new Set([...prev, ...eligibleEvacuees]));
    setLoading(true);
    
    try {
      const updatedCheckOuts = new Map();
      for (const evacueeId of eligibleEvacuees) {
        const checkOutTime = new Date().toISOString();
        const { data: logs, error } = await supabase
          .from('evacuee_check_logs')
          .select('id')
          .eq('evacuee_id', evacueeId)
          .eq('shelter_id', shelterId)
          .eq('event_id', activeEventId)
          .is('check_out', null)
          .order('check_in', { ascending: false })
          .limit(1);
          
        if (logs && logs.length > 0) {
          const logId = logs[0].id;
          await supabase
            .from('evacuee_check_logs')
            .update({ check_out: checkOutTime })
            .eq('id', logId);
            
          if (currentUser) {
            await supabase.from('audit_logs').insert([{
              user_id: currentUser.id,
              action: 'checkout evacuee',
              target_id: evacueeId,
              target_type: 'EvacuationMonitoring',
              details: { shelter_id: shelterId, event_id: activeEventId, check_out: checkOutTime }
            }]);
          }

          updatedCheckOuts.set(evacueeId, checkOutTime);
        }
      }

      setEvacuees(prev => prev.map(evacuee => {
        if (eligibleEvacuees.includes(evacuee.id) && evacuee.latest_log) {
          const checkOutTime = updatedCheckOuts.get(evacuee.id);
          return {
            ...evacuee,
            latest_log: {
              ...(evacuee.latest_log || {}),
              check_out: checkOutTime || evacuee.latest_log.check_out,
              event_id: activeEventId
            }
          };
        }
        return evacuee;
      }));
      
      setSelectedEvacuees([]);
      await updateAcceptedOccupants(shelterId, activeEventId);
      fetchShelterDetails();
    } catch (err) {
      alert('Failed to check out selected evacuees.');
    } finally {
      setUpdatingEvacuees(prev => {
        const newSet = new Set(prev);
        eligibleEvacuees.forEach(id => newSet.delete(id));
        return newSet;
      });
      setLoading(false);
    }
  };

  const generateEventReport = async () => {
    if (!activeEvent) return null;

    setGeneratingReport(true);
    try {
      // Get all evacuees for this event
      const eventEvacuees = evacuees.filter(e => e.event_id === activeEventId);
      
      // Get all check logs for this event
      const { data: eventLogs, error: logsError } = await supabase
        .from('evacuee_check_logs')
        .select('*')
        .eq('event_id', activeEventId)
        .eq('shelter_id', shelterId)
        .order('check_in', { ascending: false });

      if (logsError) throw logsError;

      // Calculate statistics
      const totalRecords = eventLogs?.length || 0;
      const uniqueEvacuees = new Set(eventLogs?.map(log => log.evacuee_id) || []).size;
      const currentlySheltered = eventLogs?.filter(log => log.check_in && !log.check_out).length || 0;
      
      // Calculate average stay duration for completed visits
      const completedVisits = eventLogs?.filter(log => log.check_in && log.check_out) || [];
      let avgStayHours = 0;
      if (completedVisits.length > 0) {
        const totalHours = completedVisits.reduce((sum, log) => {
          const checkIn = new Date(log.check_in);
          const checkOut = new Date(log.check_out);
          return sum + (checkOut - checkIn) / (1000 * 60 * 60); // Convert to hours
        }, 0);
        avgStayHours = totalHours / completedVisits.length;
      }

      const avgStayDisplay = avgStayHours > 0 
        ? avgStayHours >= 24 
          ? `${Math.round(avgStayHours / 24 * 10) / 10} days`
          : `${Math.round(avgStayHours * 10) / 10} hours`
        : 'N/A';

      return {
        event: activeEvent,
        shelter: { name: shelterName },
        statistics: {
          totalRecords,
          uniqueEvacuees,
          currentlySheltered,
          avgStayDisplay
        },
        logs: eventLogs || [],
        evacuees: eventEvacuees,
        generatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report data.');
      return null;
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleGenerateReport = async () => {
    const reportData = await generateEventReport();
    if (reportData) {
      // The modal will handle the PDF generation
      setIsReportModalOpen(true);
    }
  };

  const handleCheckInSelected = async () => {
    if (selectedEvacuees.length === 0) return;
    if (!activeEventId) {
      alert('Select an evacuation event before checking in evacuees.');
      return;
    }
    if (!activeEvent) {
      alert('No evacuation event selected.');
      return;
    }
    if (!isEventActive) {
      alert('The selected evacuation event is not active.');
      return;
    }

    const eligibleEvacuees = selectedEvacuees.filter(evacueeId => {
      const evac = evacuees.find(e => e.id === evacueeId);
      return evac && !isCheckedInAnywhere(evac);
    });

    if (eligibleEvacuees.length === 0) {
      alert('None of the selected evacuees can be checked in (they may already be checked in).');
      return;
    }

    if (!window.confirm(`Check in ${eligibleEvacuees.length} selected evacuees?`)) {
      return;
    }

    setUpdatingEvacuees(prev => new Set([...prev, ...eligibleEvacuees]));
    setLoading(true);
    
    try {
      for (const evacueeId of eligibleEvacuees) {
        const checkInTime = new Date().toISOString();
        
        // Create check-in log
        const { error: logError } = await supabase
          .from('evacuee_check_logs')
          .insert({
            evacuee_id: evacueeId,
            shelter_id: shelterId,
            check_in: checkInTime,
            event_id: activeEventId,
          });

        if (logError) throw logError;
        
        // Update evacuee's event_id if not already set
        await supabase
          .from('evacuees')
          .update({ event_id: activeEventId })
          .eq('id', evacueeId);
        
        // Add audit log
        if (currentUser) {
          await supabase.from('audit_logs').insert([{
            user_id: currentUser.id,
            action: 'bulk checkin evacuee',
            target_id: evacueeId,
            target_type: 'EvacuationMonitoring',
            details: { shelter_id: shelterId, event_id: activeEventId, check_in: checkInTime }
          }]);
        }
        
        // Update local state
        setEvacuees(prev => prev.map(evacuee => {
          if (evacuee.id === evacueeId) {
            return {
              ...evacuee,
              event_id: activeEventId,
              event_info: activeEvent ? {
                id: activeEvent.id,
                name: activeEvent.name,
                status: activeEvent.status
              } : evacuee.event_info,
              latest_log: {
                ...(evacuee.latest_log || {}),
                check_in: checkInTime,
                check_out: null,
                event_id: activeEventId
              }
            };
          }
          return evacuee;
        }));
      }

      await updateAcceptedOccupants(shelterId, activeEventId);
      fetchShelterDetails();
      alert(`Successfully checked in ${eligibleEvacuees.length} evacuees!`);
      setSelectedEvacuees([]);
    } catch (err) {
      console.error('Error checking in selected evacuees:', err);
      alert('Failed to check in selected evacuees.');
    } finally {
      setUpdatingEvacuees(prev => {
        const newSet = new Set(prev);
        eligibleEvacuees.forEach(id => newSet.delete(id));
        return newSet;
      });
      setLoading(false);
    }
  };

  const handleCheckOutAll = async () => {
    if (!activeEventId) {
      alert('Select an evacuation event before checking out evacuees.');
      return;
    }
    if (!activeEvent) {
      alert('No evacuation event selected.');
      return;
    }

    const checkedInEvacuees = filteredCheckedIn.filter(evacuee => isCheckedInCurrentEvent(evacuee));
    
    if (checkedInEvacuees.length === 0) {
      alert('No evacuees are currently checked in for this event.');
      return;
    }

    if (!window.confirm(`Check out all ${checkedInEvacuees.length} currently checked in evacuees?`)) {
      return;
    }

    const evacueeIds = checkedInEvacuees.map(e => e.id);
    setUpdatingEvacuees(prev => new Set([...prev, ...evacueeIds]));
    setLoading(true);
    
    try {
      const updatedCheckOuts = new Map();
      for (const evacuee of checkedInEvacuees) {
        const checkOutTime = new Date().toISOString();
        const { data: logs, error } = await supabase
          .from('evacuee_check_logs')
          .select('id')
          .eq('evacuee_id', evacuee.id)
          .eq('shelter_id', shelterId)
          .eq('event_id', activeEventId)
          .is('check_out', null)
          .order('check_in', { ascending: false })
          .limit(1);
          
        if (logs && logs.length > 0) {
          const logId = logs[0].id;
          await supabase
            .from('evacuee_check_logs')
            .update({ check_out: checkOutTime })
            .eq('id', logId);
            
          if (currentUser) {
            await supabase.from('audit_logs').insert([{
              user_id: currentUser.id,
              action: 'checkout all evacuees',
              target_id: evacuee.id,
              target_type: 'EvacuationMonitoring',
              details: { shelter_id: shelterId, event_id: activeEventId, check_out: checkOutTime }
            }]);
          }

          updatedCheckOuts.set(evacuee.id, checkOutTime);
        }
      }

      setEvacuees(prev => prev.map(e => {
        if (evacueeIds.includes(e.id) && e.latest_log) {
          const checkOutTime = updatedCheckOuts.get(e.id);
          return {
            ...e,
            latest_log: {
              ...(e.latest_log || {}),
              check_out: checkOutTime || e.latest_log.check_out,
              event_id: activeEventId
            }
          };
        }
        return e;
      }));
      
      setSelectedEvacuees([]);
      await updateAcceptedOccupants(shelterId, activeEventId);
      fetchShelterDetails();
      alert(`Successfully checked out all ${checkedInEvacuees.length} evacuees!`);
    } catch (err) {
      console.error('Error checking out all evacuees:', err);
      alert('Failed to check out all evacuees.');
    } finally {
      setUpdatingEvacuees(prev => {
        const newSet = new Set(prev);
        evacueeIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setLoading(false);
    }
  };

  async function updateAcceptedOccupants(shelterId, eventId) {
    if (!shelterId) return;

    if (!eventId) {
      await supabase
        .from('evacuation_shelters')
        .update({ accepted_occupants: 0 })
        .eq('id', shelterId);
      return;
    }

    const { data: evacuees, error: evacueesError } = await supabase
      .from('evacuees')
      .select('id, status')
      .eq('shelter_id', shelterId)
      .eq('event_id', eventId);

    if (evacueesError) return;

    const activeEvacuees = evacuees.filter(e => e.status === 'active');
    const evacueeIds = activeEvacuees.map(e => e.id);
    
    if (evacueeIds.length === 0) {
      await supabase
        .from('evacuation_shelters')
        .update({ accepted_occupants: 0 })
        .eq('id', shelterId);
      return;
    }

    const { data: logs, error: logsError } = await supabase
      .from('evacuee_check_logs')
      .select('evacuee_id, check_in, check_out')
      .in('evacuee_id', evacueeIds)
      .eq('shelter_id', shelterId)
      .eq('event_id', eventId);

    if (logsError) return;

    const latestLogMap = {};
    logs.forEach(log => {
      const curr = latestLogMap[log.evacuee_id];
      if (!curr || new Date(log.check_in) > new Date(curr.check_in)) {
        latestLogMap[log.evacuee_id] = log;
      }
    });

    const checkedInCount = Object.values(latestLogMap).filter(log => !log.check_out).length;

    await supabase
      .from('evacuation_shelters')
      .update({ accepted_occupants: checkedInCount })
      .eq('id', shelterId);
  }

  const isCheckedInAnywhere = (evacuee) => {
    const latest = evacuee?.latest_log;
    return Boolean(latest && latest.check_in && !latest.check_out);
  };

  const isCheckedInCurrentEvent = (evacuee) => {
    if (!activeEventId) return false;
    const latest = evacuee?.latest_log;
    return Boolean(isCheckedInAnywhere(evacuee) && latest && latest.event_id === activeEventId);
  };

  const evacueesForActiveEvent = useMemo(() => {
    if (!activeEventId) return [];
    return evacuees.filter(evacuee => {
      if (evacuee.event_id === activeEventId) return true;
      const latest = evacuee.latest_log;
      return Boolean(latest && latest.event_id === activeEventId);
    });
  }, [evacuees, activeEventId]);

  const checkedInForActiveEvent = useMemo(() => {
    if (!activeEventId) return [];
    return evacueesForActiveEvent.filter(evacuee => {
      const latest = evacuee?.latest_log;
      return Boolean(
        latest &&
        latest.event_id === activeEventId &&
        latest.check_in &&
        !latest.check_out
      );
    });
  }, [evacueesForActiveEvent, activeEventId]);

  // Filter and pagination logic
  const filteredEvacuees = evacuees.filter(evacuee => {
    const matchesSearch =
      searchTerm.trim() === '' ||
      (evacuee.first_name && evacuee.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (evacuee.last_name && evacuee.last_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // When searching, show all regardless of status
    if (searchTerm.trim() !== '') {
      return matchesSearch && evacuee.status === 'active';
    }
    
    // Otherwise apply status filter
    const latest = evacuee.latest_log;
    let matchesStatus = true;
    if (statusFilter === 'checkedin') {
      matchesStatus = isCheckedInAnywhere(evacuee);
    } else if (statusFilter === 'checkedout') {
      matchesStatus = latest && latest.check_in && latest.check_out;
    }
    
    return matchesSearch && matchesStatus && evacuee.status === 'active';
  });

  // Get checked-in evacuees with search
  const filteredCheckedIn = evacuees.filter(evacuee => {
    const matchesSearch =
      checkedInSearchTerm.trim() === '' ||
      (evacuee.first_name && evacuee.first_name.toLowerCase().includes(checkedInSearchTerm.toLowerCase())) ||
      (evacuee.last_name && evacuee.last_name.toLowerCase().includes(checkedInSearchTerm.toLowerCase()));
    
    return matchesSearch && 
           evacuee.status === 'active' && 
           isCheckedInAnywhere(evacuee) &&
           (!activeEventId || (evacuee.latest_log && evacuee.latest_log.event_id === activeEventId));
  });

  const currentEvacuees = currentTab === 'evacuees' ? filteredEvacuees : filteredCheckedIn;
  const currentPage = currentTab === 'evacuees' ? evacueePage : checkedInPage;
  const setCurrentPage = currentTab === 'evacuees' ? setEvacueePage : setCheckedInPage;

  const totalPages = Math.ceil(currentEvacuees.length / itemsPerPage);
  const paginatedEvacuees = currentEvacuees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Calculate select all state - check if ALL items on current page are selected
  const isAllSelected = paginatedEvacuees.length > 0 && 
                       paginatedEvacuees.every(evacuee => selectedEvacuees.includes(evacuee.id));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric' 
    }) + ', ' + date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getStatusBadge = (evacuee) => {
    const latest = evacuee.latest_log;
    if (!latest || !latest.check_in) {
      return { text: 'Not Checked In', color: '#f59e0b', bg: '#fffbeb' };
    }
    if (latest.check_out) {
      return { text: 'Checked Out', color: '#ef4444', bg: '#fef2f2' };
    }
    if (activeEventId && latest.event_id && latest.event_id !== activeEventId) {
      return { text: 'Checked In (Other Event)', color: '#6366f1', bg: '#eef2ff' };
    }
    return { text: 'Checked In', color: '#10b981', bg: '#ecfdf5' };
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div className="evac-monitor-content">
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <button 
              onClick={() => navigate('/evacuation-shelters')}
              className="back-button"
            >
              <FaArrowLeft />
            </button>
            <div className="title-text">Evacuee Monitoring for {shelterName}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn btn-primary"
              disabled={addButtonDisabled}
              title={
                addButtonDisabled
                  ? (!isEventSelected
                    ? 'Create or select an evacuation event to enable registration.'
                    : 'This evacuation event is completed. Activate a new event to continue registering evacuees.')
                  : 'Register a new evacuee or family for this event.'
              }
            >
              <FaUserPlus /> Add Evacuee/Family
            </button>
            <ProfileMenu />
          </div>
        </div>

        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {/* Dashboard Statistics */}
        <div className="evac-card" style={{ marginBottom: '24px' }}>
          <div className="event-card-header" style={{ padding: '24px 24px 0 24px', marginBottom: 0 }}>
            <div className="event-card-title">
              <FaInfoCircle className="event-card-icon" />
              Dashboard Statistics
            </div>
          </div>
          <div className="dashboard-container" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            padding: '24px'
          }}>
          {/* Number of Evacuees */}
          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '1px solid #cfe8ff',
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            boxShadow: '0 8px 20px rgba(8, 177, 199, 0.08)'
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#0c4a6e' }}>
              Number of Evacuees
            </span>
            <span style={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>
              {dashboardStats.numberOfEvacuees}
            </span>
            <span style={{ fontSize: 12, color: '#0c4a6e' }}>
              Active evacuees in this shelter
            </span>
          </div>

          {/* Number of Families */}
          <div style={{
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '1px solid #bbf7d0',
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.08)'
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#047857' }}>
              Number of Families
            </span>
            <span style={{ fontSize: 28, fontWeight: 700, color: '#064e3b' }}>
              {dashboardStats.numberOfFamilies}
            </span>
            <span style={{ fontSize: 12, color: '#047857' }}>
              Unique families registered
            </span>
          </div>

          {/* Checked In */}
          <div style={{
            background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)',
            border: '1px solid #fde68a',
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            boxShadow: '0 8px 20px rgba(245, 158, 11, 0.08)'
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#b45309' }}>
              Checked In
            </span>
            <span style={{ fontSize: 28, fontWeight: 700, color: '#92400e' }}>
              {dashboardStats.checkedIn}
            </span>
            <span style={{ fontSize: 12, color: '#b45309' }}>
              Currently present in shelter
            </span>
          </div>

          {/* Checked Out */}
          <div style={{
            background: 'linear-gradient(135deg, #f3f4ff 0%, #e0e7ff 100%)',
            border: '1px solid #c7d2fe',
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            boxShadow: '0 8px 20px rgba(79, 70, 229, 0.08)'
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#4338ca' }}>
              Checked Out
            </span>
            <span style={{ fontSize: 28, fontWeight: 700, color: '#312e81' }}>
              {dashboardStats.checkedOut}
            </span>
            <span style={{ fontSize: 12, color: '#4338ca' }}>
              Evacuees who have left
            </span>
          </div>
          </div>
        </div>

        <div className="event-card">
          <div className="event-card-header">
            <div className="event-card-title">
              <FaCalendarAlt className="event-card-icon" />
              Evacuation Events
            </div>
            <div className="event-card-actions">
              {isEventActive && (
                <button
                  onClick={handleCompleteEvent}
                  className="btn btn-warning btn-sm"
                  disabled={completingEvent}
                  title="Mark this event as completed"
                >
                  {completingEvent ? (
                    <div className="loading-spinner"></div>
                  ) : (
                    <FaFlag />
                  )}
                  {completingEvent ? ' Completing...' : ' Complete Event'}
                </button>
              )}
              <button
                onClick={handleOpenEventModal}
                className="btn btn-secondary btn-sm"
                disabled={hasActiveEvent}
                title={hasActiveEvent ? 'Complete or archive the current active event before creating a new one' : 'Create a new evacuation event'}
              >
                <FaPlus /> Create Event
              </button>
              {activeEvent && (
                <button
                  onClick={handleGenerateReport}
                  className="btn btn-info btn-sm"
                  disabled={generatingReport}
                  title="Generate report for selected event"
                >
                  {generatingReport ? (
                    <div className="loading-spinner"></div>
                  ) : (
                    <FaFileAlt />
                  )}
                  {generatingReport ? ' Generating...' : ' Generate Report'}
                </button>
              )}
            </div>
          </div>
          <div className="event-card-body">
            {eventLoading ? (
              <div className="event-card-loading">Loading evacuation events...</div>
            ) : eventError ? (
              <div className="event-card-error">
                <FaInfoCircle className="event-error-icon" />
                {eventError}
              </div>
            ) : events.length === 0 ? (
              <div className="event-card-empty">
                <FaInfoCircle className="event-empty-icon" />
                <span>Create your first evacuation event to start tracking evacuees.</span>
              </div>
            ) : (
              <>
                <div className="event-select-group">
                  <label htmlFor="event-select" className="event-select-label">
                    Active Event
                  </label>
                  <select
                    id="event-select"
                    className="event-select"
                    value={activeEventId || ''}
                    onChange={(e) => handleSelectEvent(e.target.value)}
                  >
                    {!activeEventId && <option value="">Select an event</option>}
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name || 'Untitled Event'}
                        {event.status === 'completed'
                          ? ' (Completed)'
                          : event.status === 'archived'
                            ? ' (Archived)'
                            : event.status === 'active'
                              ? ' (Active)'
                              : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {!isEventActive && activeEvent && (
                  <div className="event-alert">
                    <FaInfoCircle className="event-alert-icon" />
                    <span>
                      This event is marked as {activeEvent.status || 'inactive'}. Check-in is disabled until another event is activated.
                    </span>
                  </div>
                )}

                {activeEvent && (
                  <div className="event-summary">
                    <div className="event-summary-row">
                      <span className="event-summary-label">Status</span>
                      <span className={`event-status-badge status-${activeEvent.status || 'unknown'}`}>
                        {activeEvent.status ? activeEvent.status.replace(/_/g, ' ') : 'Unknown'}
                      </span>
                    </div>
                    <div className="event-summary-row">
                      <span className="event-summary-label">Started</span>
                      <span className="event-summary-value">
                        {activeEvent.start_time ? formatDate(activeEvent.start_time) : '-'}
                      </span>
                    </div>
                    {activeEvent.end_time && (
                      <div className="event-summary-row">
                        <span className="event-summary-label">Completed</span>
                        <span className="event-summary-value">
                          {formatDate(activeEvent.end_time)}
                        </span>
                      </div>
                    )}
                    <div className="event-summary-row">
                      <span className="event-summary-label">Evacuees</span>
                      <span className="event-summary-value">
                        {evacueesForActiveEvent.length} total · {checkedInForActiveEvent.length} currently checked in
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Single Column Layout with Tabs */}
        <div className="single-column-layout">
          <div className="evac-card">
            {/* Tabs */}
            <div className="tabs-container">
              <div className="tabs">
                <button
                  className={`tab ${currentTab === 'evacuees' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentTab('evacuees');
                    setEvacueePage(1);
                    setSelectedEvacuees([]);
                  }}
                >
                  All Evacuees ({filteredEvacuees.length})
                </button>
                <button
                  className={`tab ${currentTab === 'checkedin' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentTab('checkedin');
                    setCheckedInPage(1);
                    setSelectedEvacuees([]);
                  }}
                >
                  Currently Checked In ({filteredCheckedIn.length})
                </button>
              </div>
              
              {currentTab === 'evacuees' && (
                <div className="tab-actions">
                  <button
                    onClick={handleCheckInSelected}
                    disabled={selectedEvacuees.length === 0 || loading || !canManageAttendance}
                    className="btn btn-success"
                    title={
                      !canManageAttendance
                        ? 'Activate an evacuation event to enable check-ins.'
                        : selectedEvacuees.length === 0
                          ? 'Select evacuees to check in.'
                          : 'Check In Selected'
                    }
                  >
                    <FaSignInAlt /> Check In Selected ({selectedEvacuees.length})
                  </button>
                  <button
                    onClick={handleArchiveSelected}
                    disabled={selectedEvacuees.length === 0 || loading}
                    className="btn btn-danger"
                  >
                    <FaArchive /> Archive Selected ({selectedEvacuees.length})
                  </button>
                  <button
                    onClick={handleCheckOutSelected}
                    disabled={selectedEvacuees.length === 0 || loading || !canManageAttendance}
                    className="btn btn-warning"
                    title={
                      !canManageAttendance
                        ? 'Activate an evacuation event to enable check-outs.'
                        : selectedEvacuees.length === 0
                          ? 'Select evacuees to check out.'
                          : 'Check Out Selected'
                    }
                  >
                    <FaSignOutAlt /> Check Out Selected ({selectedEvacuees.length})
                  </button>
                </div>
              )}

              {currentTab === 'checkedin' && (
                <div className="tab-actions">
                  <button
                    onClick={handleCheckOutSelected}
                    disabled={selectedEvacuees.length === 0 || loading || !canCheckOut}
                    className="btn btn-warning"
                    title={
                      !canCheckOut
                        ? 'Select an evacuation event to enable check-outs.'
                        : selectedEvacuees.length === 0
                          ? 'Select evacuees to check out.'
                          : 'Check Out All Selected'
                    }
                  >
                    <FaSignOutAlt /> Check Out Selected ({selectedEvacuees.length})
                  </button>
                  <button
                    onClick={handleCheckOutAll}
                    disabled={filteredCheckedIn.length === 0 || loading || !canCheckOut}
                    className="btn btn-danger"
                    title={
                      !canCheckOut
                        ? 'Select an evacuation event to enable check-outs.'
                        : filteredCheckedIn.length === 0
                          ? 'No evacuees are currently checked in.'
                          : `Check out all ${filteredCheckedIn.length} currently checked in evacuees`
                    }
                  >
                    <FaSignOutAlt /> Check Out All ({filteredCheckedIn.length})
                  </button>
                </div>
              )}
            </div>

            {/* Search and Filter Controls */}
            <div className="evac-controls">
              <div className="search-container">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder={`Search ${currentTab === 'evacuees' ? 'evacuees' : 'checked in evacuees'} by name...`}
                  value={currentTab === 'evacuees' ? searchTerm : checkedInSearchTerm}
                  onChange={e => currentTab === 'evacuees' ? setSearchTerm(e.target.value) : setCheckedInSearchTerm(e.target.value)}
                  className="search-input"
                />
                {(currentTab === 'evacuees' ? searchTerm : checkedInSearchTerm) && (
                  <button
                    onClick={() => currentTab === 'evacuees' ? setSearchTerm('') : setCheckedInSearchTerm('')}
                    className="clear-search-btn"
                    aria-label="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              
              {currentTab === 'evacuees' && (
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="checkedout">Checked Out</option>
                  <option value="all">All Status</option>
                  <option value="checkedin">Checked In</option>
                </select>
              )}
            </div>

            {/* List Header */}
            <div className="list-header">
              <div className="header-select">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAllClick}
                  className="select-all-checkbox"
                  ref={input => {
                    // This ensures the checkbox stays in sync
                    if (input) {
                      input.indeterminate = selectedEvacuees.length > 0 && selectedEvacuees.length < paginatedEvacuees.length;
                    }
                  }}
                />
              </div>
              <div className="header-name">Name</div>
              <div className="header-status">Status</div>
              <div className="header-checkin">Last Checked In</div>
              <div className="header-actions">Actions</div>
            </div>

            {/* Evacuee List */}
            {evacueeListLoading ? (
              <div className="loading-state">Loading evacuees...</div>
            ) : evacueeListError ? (
              <div className="error-state">
                {console.log('🚨 Displaying evacuee list error:', evacueeListError)}
                {evacueeListError}
              </div>
            ) : paginatedEvacuees.length > 0 ? (
              <div className="table-list">
                {paginatedEvacuees.map(evacuee => {
                  const statusBadge = getStatusBadge(evacuee);
                  const isSelected = selectedEvacuees.includes(evacuee.id);
                  const isUpdating = updatingEvacuees.has(evacuee.id);
                  const checkedInAnywhere = isCheckedInAnywhere(evacuee);
                  const checkedInCurrent = isCheckedInCurrentEvent(evacuee);
                  const eventDescriptor = evacuee.event_info
                    ? `Event: ${evacuee.event_info.name}${evacuee.event_info.status ? ` (${evacuee.event_info.status})` : ''}`
                    : (evacuee.event_id ? 'Event: (Unknown)' : 'Event: Not Assigned');
                  
                  return (
                    <div key={evacuee.id} className="table-row">
                      {/* Selection Indicator */}
                      <div className="row-select">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(evacuee.id)}
                          className="row-checkbox"
                        />
                      </div>

                      {/* Name */}
                      <div className="row-name">
                        <div className="name-text">
                          {evacuee.first_name} {evacuee.last_name}
                        </div>
                        <div className="name-subtext">
                          Age: {evacuee.age} • Family: {evacuee.family_count || 1}{' '}
                          {eventDescriptor ? `• ${eventDescriptor}` : ''}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="row-status">
                        <div
                          className="status-badge"
                          style={{
                            color: statusBadge.color,
                            background: statusBadge.bg,
                            border: `1px solid ${statusBadge.color}`
                          }}
                        >
                          {statusBadge.text}
                        </div>
                      </div>

                      {/* Last Checked In */}
                      <div className="row-checkin">
                        {evacuee.latest_log && evacuee.latest_log.check_in ? 
                          formatDate(evacuee.latest_log.check_in) : '-'}
                      </div>

                      {/* Actions */}
                      <div className="row-actions">
                        <button
                          onClick={() => handleViewDetails(evacuee.id)}
                          className="btn btn-secondary btn-sm"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        
                        {currentTab === 'evacuees' && (
                          <>
                            <button
                              onClick={() => handleCheckIn(evacuee.id)}
                              disabled={checkedInAnywhere || isUpdating || !canManageAttendance}
                              className="btn btn-success btn-sm"
                              title={
                                !canManageAttendance
                                  ? 'Activate an evacuation event to enable check-ins.'
                                  : checkedInAnywhere
                                    ? 'Evacuee is already checked in.'
                                    : 'Check In'
                              }
                            >
                              {isUpdating ? (
                                <div className="loading-spinner"></div>
                              ) : (
                                <FaSignInAlt />
                              )}
                            </button>
                            <button
                              onClick={() => handleCheckOut(evacuee.id)}
                              disabled={!checkedInCurrent || isUpdating || !canManageAttendance}
                              className="btn btn-warning btn-sm"
                              title={
                                !canManageAttendance
                                  ? 'Activate an evacuation event to enable check-outs.'
                                  : checkedInCurrent
                                    ? 'Check Out'
                                    : 'Evacuee is not checked in for this event.'
                              }
                            >
                              {isUpdating ? (
                                <div className="loading-spinner"></div>
                              ) : (
                                <FaSignOutAlt />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedEvacuees([evacuee.id]);
                                setTimeout(() => handleArchiveSelected(), 0);
                              }}
                              className="btn btn-danger btn-sm"
                              title="Archive"
                            >
                              <FaArchive />
                            </button>
                          </>
                        )}
                        
                        {currentTab === 'checkedin' && (
                          <button
                            onClick={() => handleCheckOut(evacuee.id)}
                            disabled={!checkedInCurrent || isUpdating || !canCheckOut}
                            className="btn btn-warning btn-sm"
                            title={
                              !canCheckOut
                                ? 'Select an evacuation event to enable check-outs.'
                                : checkedInCurrent
                                  ? 'Check Out'
                                  : 'Evacuee is not checked in for this event.'
                            }
                          >
                            {isUpdating ? (
                              <div className="loading-spinner"></div>
                            ) : (
                              <FaSignOutAlt />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                {currentTab === 'evacuees' ? 'No evacuees found.' : 'No one is currently checked in.'}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`pagination-number ${currentPage === i + 1 ? 'active' : ''}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Create Event Modal */}
      {isEventModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create Evacuation Event</h2>
              <button
                type="button"
                onClick={handleCloseEventModal}
                className="modal-close-btn"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCreateEvent} className="modal-form">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label">
                    <FaCalendarAlt className="input-icon" />
                    Event Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={eventForm.name}
                    onChange={handleEventFormChange}
                    placeholder="Enter evacuation event name"
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <FaClock className="input-icon" />
                    Start Date &amp; Time
                  </label>
                  <input
                    type="datetime-local"
                    name="startDate"
                    value={eventForm.startDate}
                    onChange={handleEventFormChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">
                    <FaInfoCircle className="input-icon" />
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    value={eventForm.description}
                    onChange={handleEventFormChange}
                    placeholder="Add details about this evacuation event"
                    className="form-textarea"
                    rows={4}
                  ></textarea>
                </div>
              </div>

              {eventFormError && (
                <div className="form-error">
                  <FaTimes className="error-icon" />
                  {eventFormError}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCloseEventModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creatingEvent}
                >
                  {creatingEvent ? (
                    <>
                      <div className="loading-spinner"></div>
                      Creating Event...
                    </>
                  ) : (
                    <>
                      <FaPlus /> Create Event
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Evacuee/Family Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h2>Register Family</h2>
              <button 
                type="button" 
                onClick={handleCloseAddModal}
                className="modal-close-btn"
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <div className="form-section-header">
                  <div className="form-section-title">
                    <FaUsers className="section-icon" />
                    Family Members Information
                  </div>
                  <button
                    type="button"
                    onClick={addFamilyMember}
                    className="btn btn-primary btn-sm"
                  >
                    <FaPlus /> Add Family Member
                  </button>
                </div>
                
                <div className="family-members-list">
                  {familyMembers.map((member, index) => (
                    <div key={index} className="family-member-card">
                      <div className="member-card-header">
                        <div className="member-title">
                          <FaUser className="member-icon" />
                          Family Member {index + 1}
                        </div>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => removeFamilyMember(index)}
                            className="btn btn-danger btn-sm"
                          >
                            <FaTimes /> Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="member-form-grid">
                        <div className="form-group">
                          <label className="form-label">
                            <FaUser className="input-icon" />
                            First Name *
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            placeholder="Enter first name"
                            value={member.firstName}
                            onChange={(e) => handleChange(e, index)}
                            required
                            className="form-input"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <FaUser className="input-icon" />
                            Last Name *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            placeholder="Enter last name"
                            value={member.lastName}
                            onChange={(e) => handleChange(e, index)}
                            required
                            className="form-input"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <FaBirthdayCake className="input-icon" />
                            Age *
                          </label>
                          <input
                            type="number"
                            name="age"
                            placeholder="Enter age"
                            value={member.age}
                            onChange={(e) => handleChange(e, index)}
                            required
                            min="0"
                            max="120"
                            className="form-input"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <FaVenusMars className="input-icon" />
                            Gender *
                          </label>
                          <select
                            name="gender"
                            value={member.gender}
                            onChange={(e) => handleChange(e, index)}
                            required
                            className="form-select"
                          >
                            <option value="">Select gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        
                        <div className="form-group full-width">
                          <label className="form-label">
                            <FaIdCard className="input-icon" />
                            Identification Number (Optional)
                          </label>
                          <input
                            type="text"
                            name="identificationNumber"
                            placeholder="Enter identification number"
                            value={member.identificationNumber}
                            onChange={(e) => handleChange(e, index)}
                            className="form-input"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {error && (
                <div className="form-error">
                  <FaTimes className="error-icon" />
                  {error}
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseAddModal} 
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={registerLoading}
                >
                  {registerLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Registering Family...
                    </>
                  ) : (
                    <>
                      <FaUserPlus />
                      Register Family
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Evacuee Modal */}
      {isEditModalOpen && currentEvacuee && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Evacuee</h2>
              <button 
                type="button" 
                onClick={handleCloseEditModal}
                className="modal-close-btn"
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSaveEdit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <FaUser className="input-icon" />
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={currentEvacuee.first_name || ''}
                    onChange={handleEditFormChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <FaUser className="input-icon" />
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={currentEvacuee.last_name || ''}
                    onChange={handleEditFormChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <FaBirthdayCake className="input-icon" />
                    Age *
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={currentEvacuee.age || ''}
                    onChange={handleEditFormChange}
                    required
                    min="0"
                    max="120"
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <FaVenusMars className="input-icon" />
                    Gender *
                  </label>
                  <select
                    name="gender"
                    value={currentEvacuee.gender || ''}
                    onChange={handleEditFormChange}
                    required
                    className="form-select"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="form-group full-width">
                  <label className="form-label">
                    <FaIdCard className="input-icon" />
                    Identification Number (Optional)
                  </label>
                  <input
                    type="text"
                    name="identification_number"
                    value={currentEvacuee.identification_number || ''}
                    onChange={handleEditFormChange}
                    className="form-input"
                  />
                </div>
              </div>
              
              {error && (
                <div className="form-error">
                  <FaTimes className="error-icon" />
                  {error}
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseEditModal} 
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={editLoading}
                  className="btn btn-primary"
                >
                  {editLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaCheck />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Detail View Modal */}
      {isDetailModalOpen && currentEvacuee && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Evacuee Details</h2>
              <button 
                type="button" 
                onClick={handleCloseDetailModal}
                className="modal-close-btn"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="detail-section">
                <h3 className="detail-section-title">Personal Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <FaUser className="detail-icon" />
                    <span className="detail-label">Full Name:</span>
                    <span className="detail-value">{currentEvacuee.first_name} {currentEvacuee.last_name}</span>
                  </div>
                  <div className="detail-item">
                    <FaBirthdayCake className="detail-icon" />
                    <span className="detail-label">Age:</span>
                    <span className="detail-value">{currentEvacuee.age}</span>
                  </div>
                  <div className="detail-item">
                    <FaVenusMars className="detail-icon" />
                    <span className="detail-label">Gender:</span>
                    <span className="detail-value">{currentEvacuee.gender}</span>
                  </div>
                  <div className="detail-item">
                    <FaUsers className="detail-icon" />
                    <span className="detail-label">Family Members:</span>
                    <span className="detail-value">{currentEvacuee.family_count || 1}</span>
                  </div>
                  {currentEvacuee.event_info ? (
                    <div className="detail-item">
                      <FaCalendarAlt className="detail-icon" />
                      <span className="detail-label">Evacuation Event:</span>
                      <span className="detail-value">
                        {currentEvacuee.event_info.name}
                        {currentEvacuee.event_info.status ? ` (${currentEvacuee.event_info.status})` : ''}
                      </span>
                    </div>
                  ) : (
                    <div className="detail-item">
                      <FaCalendarAlt className="detail-icon" />
                      <span className="detail-label">Evacuation Event:</span>
                      <span className="detail-value">
                        {currentEvacuee.event_id ? 'Unresolved event record' : 'Not assigned'}
                      </span>
                    </div>
                  )}
                  {currentEvacuee.identification_number && (
                    <div className="detail-item">
                      <FaIdCard className="detail-icon" />
                      <span className="detail-label">ID Number:</span>
                      <span className="detail-value">{currentEvacuee.identification_number}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="detail-section">
                <h3 className="detail-section-title">Check-in History</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <FaSignInAlt className="detail-icon" />
                    <span className="detail-label">Last Check-in:</span>
                    <span className="detail-value">
                      {currentEvacuee.latest_log && currentEvacuee.latest_log.check_in ? 
                        formatDate(currentEvacuee.latest_log.check_in) : 'Not yet'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <FaSignOutAlt className="detail-icon" />
                    <span className="detail-label">Last Check-out:</span>
                    <span className="detail-value">
                      {currentEvacuee.latest_log && currentEvacuee.latest_log.check_out ? 
                        formatDate(currentEvacuee.latest_log.check_out) : 'Not yet'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Current Status:</div>
                    <div className="detail-value">
                      <div
                        className="status-badge"
                        style={getStatusBadge(currentEvacuee)}
                      >
                        {getStatusBadge(currentEvacuee).text}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="detail-actions">
                <button
                  onClick={() => {
                    handleCloseDetailModal();
                    handleEditEvacuee(currentEvacuee.id);
                  }}
                  className="btn btn-secondary"
                >
                  <FaEdit /> Edit Evacuee
                </button>
                {isCheckedInCurrentEvent(currentEvacuee) ? (
                  <button
                    onClick={() => {
                      handleCloseDetailModal();
                      handleCheckOut(currentEvacuee.id);
                    }}
                    disabled={
                      updatingEvacuees.has(currentEvacuee.id) ||
                      !canManageAttendance
                    }
                    className="btn btn-warning"
                  >
                    {updatingEvacuees.has(currentEvacuee.id) ? (
                      <>
                        <div className="loading-spinner"></div>
                        Checking Out...
                      </>
                    ) : (
                      <>
                        <FaSignOutAlt /> Check Out
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleCloseDetailModal();
                      handleCheckIn(currentEvacuee.id);
                    }}
                    disabled={
                      updatingEvacuees.has(currentEvacuee.id) ||
                      isCheckedInAnywhere(currentEvacuee) ||
                      !canManageAttendance
                    }
                    className="btn btn-success"
                  >
                    {updatingEvacuees.has(currentEvacuee.id) ? (
                      <>
                        <div className="loading-spinner"></div>
                        Checking In...
                      </>
                    ) : (
                      <>
                        <FaSignInAlt /> Check In
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Report Modal */}
      {isReportModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '1000px', width: '95%', maxHeight: '90vh' }}>
            <div className="modal-header">
              <h2>Event Report - {activeEvent?.name}</h2>
              <button
                type="button"
                onClick={() => setIsReportModalOpen(false)}
                className="modal-close-btn"
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-content" style={{ padding: '24px', overflow: 'auto' }}>
              <EventReportContent 
                event={activeEvent}
                shelterName={shelterName}
                currentUser={currentUser}
                evacuees={evacuees}
                shelterId={shelterId}
                onClose={() => setIsReportModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EvacuationMonitoring;